import React, { useState } from 'react';
import './AttendanceDetailView.css';

function AttendanceDetailView({ attendanceRecord, student, onClose }) {
  const [showCalendar, setShowCalendar] = useState(false);

  if (!attendanceRecord) {
    return (
      <div className="attendance-detail-overlay" onClick={onClose}>
        <div className="attendance-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Attendance Details</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-content">
            <p>No attendance information available</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusInfo = (status) => {
    switch (status?.toLowerCase()) {
      case 'present':
        return { 
          icon: '✅', 
          color: '#28a745', 
          bgColor: '#d4edda', 
          label: 'Present',
          description: 'Student was present in class'
        };
      case 'absent':
        return { 
          icon: '❌', 
          color: '#dc3545', 
          bgColor: '#f8d7da', 
          label: 'Absent',
          description: 'Student was absent from class'
        };
      case 'tardy':
        return { 
          icon: '⏰', 
          color: '#ffc107', 
          bgColor: '#fff3cd', 
          label: 'Tardy',
          description: 'Student arrived late to class'
        };
      case 'excused':
        return { 
          icon: '📋', 
          color: '#17a2b8', 
          bgColor: '#dcfce7', 
          label: 'Excused',
          description: 'Absence was excused by parent/guardian'
        };
      case 'early_dismissal':
        return { 
          icon: '🏃', 
          color: '#fd7e14', 
          bgColor: '#fed7aa', 
          label: 'Early Dismissal',
          description: 'Student left class early'
        };
      default:
        return { 
          icon: '❓', 
          color: '#6c757d', 
          bgColor: '#e2e3e5', 
          label: 'Unknown',
          description: 'Status not specified'
        };
    }
  };

  const getAttendanceRate = () => {
    // This would typically come from student data or be calculated
    return attendanceRecord.attendanceRate || 'N/A';
  };

  const statusInfo = getStatusInfo(attendanceRecord.status);

  return (
    <div className="attendance-detail-overlay" onClick={onClose}>
      <div className="attendance-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Attendance Details</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-content">
          {/* Attendance Overview */}
          <div className="section">
            <h4>Attendance Overview</h4>
            <div className="attendance-overview">
              <div className="status-display">
                <div className="status-icon" style={{ backgroundColor: statusInfo.bgColor }}>
                  <span style={{ fontSize: '32px' }}>{statusInfo.icon}</span>
                </div>
                <div className="status-info">
                  <h3>{statusInfo.label}</h3>
                  <p>{statusInfo.description}</p>
                </div>
              </div>
              
              <div className="attendance-stats">
                <div className="stat-item">
                  <label>Date:</label>
                  <span>
                    {attendanceRecord.date?.toDate?.() ? 
                      new Date(attendanceRecord.date.toDate()).toLocaleDateString() : 
                      attendanceRecord.date ? new Date(attendanceRecord.date).toLocaleDateString() : 
                      'N/A'
                    }
                  </span>
                </div>
                {(() => {
                  const timeStr = attendanceRecord.createdAt?.toDate?.() ? 
                    new Date(attendanceRecord.createdAt.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                    attendanceRecord.createdAt ? new Date(attendanceRecord.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                    attendanceRecord.updatedAt?.toDate?.() ? 
                      new Date(attendanceRecord.updatedAt.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                      null;
                  return timeStr ? (
                    <div className="stat-item">
                      <label>Marked At:</label>
                      <span>{timeStr}</span>
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          </div>

          {/* Student Information */}
          <div className="section">
            <h4>Student Information</h4>
            <div className="student-details">
              <div className="student-avatar">
                <span>👤</span>
              </div>
              <div className="student-info">
                <div className="student-name">{student?.fullName || 'N/A'}</div>
                <div className="student-meta">
                  <span>ID: {student?.studentId || 'N/A'}</span>
                  <span>Class: {student?.className || student?.classInfo?.name || 'N/A'}</span>
                  {student?.classInfo?.grade && <span>{student.classInfo.grade}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Attendance Details */}
          <div className="section">
            <h4>Attendance Details</h4>
            <div className="details-grid">
              <div className="detail-item">
                <label>Status:</label>
                <span className={`status-badge ${attendanceRecord.status || 'unknown'}`} style={{ backgroundColor: statusInfo.bgColor, color: statusInfo.color }}>
                  {statusInfo.icon} {statusInfo.label}
                </span>
              </div>
              <div className="detail-item">
                <label>Marked By:</label>
                <span>{attendanceRecord.markedBy || 'Teacher'}</span>
              </div>
              <div className="detail-item">
                <label>Recorded:</label>
                <span>
                  {attendanceRecord.createdAt?.toDate?.() ? 
                    new Date(attendanceRecord.createdAt.toDate()).toLocaleString() : 
                    attendanceRecord.createdAt ? new Date(attendanceRecord.createdAt).toLocaleString() : 
                    'N/A'
                  }
                </span>
              </div>
              {attendanceRecord.updatedAt && (
                <div className="detail-item">
                  <label>Last Updated:</label>
                  <span>
                    {attendanceRecord.updatedAt?.toDate?.() ? 
                      new Date(attendanceRecord.updatedAt.toDate()).toLocaleString() : 
                      new Date(attendanceRecord.updatedAt).toLocaleString()
                    }
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {attendanceRecord.notes && (
            <div className="section">
              <h4>Notes</h4>
              <div className="comments-section">
                <div className="comment-box">
                  <p>{attendanceRecord.notes}</p>
                  <small className="comment-meta">
                    {attendanceRecord.markedBy ? `Added by teacher` : 'Added by teacher'} • 
                    {attendanceRecord.createdAt?.toDate?.() ? 
                      ` ${new Date(attendanceRecord.createdAt.toDate()).toLocaleDateString()}` : 
                      ' Date not available'
                    }
                  </small>
                </div>
              </div>
            </div>
          )}

          {/* Excuse Information */}
          {attendanceRecord.status === 'absent' && attendanceRecord.excuse && (
            <div className="section">
              <h4>Excuse Information</h4>
              <div className="excuse-details">
                <div className="excuse-header">
                  <span className="excuse-icon">📋</span>
                  <span className="excuse-label">Excused Absence</span>
                </div>
                <div className="excuse-content">
                  <p>{attendanceRecord.excuse.reason || 'No reason provided'}</p>
                  <div className="excuse-meta">
                    <div className="detail-item">
                      <label>Excused By:</label>
                      <span>{attendanceRecord.excuse.excusedBy || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Excuse Date:</label>
                      <span>
                        {attendanceRecord.excuse.date?.toDate?.() ? 
                          new Date(attendanceRecord.excuse.date.toDate()).toLocaleDateString() : 
                          'N/A'
                        }
                      </span>
                    </div>
                    {attendanceRecord.excuse.documents && attendanceRecord.excuse.documents.length > 0 && (
                      <div className="detail-item">
                        <label>Supporting Documents:</label>
                        <div className="documents-list">
                          {attendanceRecord.excuse.documents.map((doc, index) => (
                            <div key={index} className="document-item">
                              <span className="document-icon">📎</span>
                              <a href={doc.url} target="_blank" rel="noopener noreferrer" className="document-link">
                                {doc.name || 'Document'}
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Attendance History */}
          <div className="section">
            <div className="section-header">
              <h4>Recent Attendance History</h4>
              <button 
                className="toggle-calendar"
                onClick={() => setShowCalendar(!showCalendar)}
              >
                {showCalendar ? 'Hide' : 'Show'} Calendar
              </button>
            </div>
            
            <div className="attendance-summary">
              <div className="summary-stats">
                <div className="summary-item present">
                  <div className="summary-icon">✅</div>
                  <div className="summary-info">
                    <div className="summary-value">{attendanceRecord.recentStats?.present || 0}</div>
                    <div className="summary-label">Present</div>
                  </div>
                </div>
                <div className="summary-item absent">
                  <div className="summary-icon">❌</div>
                  <div className="summary-info">
                    <div className="summary-value">{attendanceRecord.recentStats?.absent || 0}</div>
                    <div className="summary-label">Absent</div>
                  </div>
                </div>
                <div className="summary-item tardy">
                  <div className="summary-icon">⏰</div>
                  <div className="summary-info">
                    <div className="summary-value">{attendanceRecord.recentStats?.tardy || 0}</div>
                    <div className="summary-label">Tardy</div>
                  </div>
                </div>
                <div className="summary-item rate">
                  <div className="summary-icon">📊</div>
                  <div className="summary-info">
                    <div className="summary-value">{getAttendanceRate()}%</div>
                    <div className="summary-label">Rate</div>
                  </div>
                </div>
              </div>
            </div>

            {showCalendar && (
              <div className="calendar-view">
                <div className="calendar-header">
                  <h5>Attendance Calendar</h5>
                  <span className="calendar-month">
                    {attendanceRecord.date?.toDate?.() ? 
                      new Date(attendanceRecord.date.toDate()).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 
                      'Current Month'
                    }
                  </span>
                </div>
                <div className="calendar-grid">
                  {/* This would be populated with actual calendar data */}
                  <div className="calendar-placeholder">
                    <p>Calendar view would show attendance patterns for the month</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Attendance Patterns */}
          <div className="section">
            <h4>Attendance Patterns</h4>
            <div className="patterns-analysis">
              <div className="pattern-item">
                <div className="pattern-label">Most Common Status:</div>
                <div className="pattern-value">
                  <span className={`status-badge ${attendanceRecord.patterns?.mostCommon || 'unknown'}`}>
                    {attendanceRecord.patterns?.mostCommon ? 
                      attendanceRecord.patterns.mostCommon.charAt(0).toUpperCase() + attendanceRecord.patterns.mostCommon.slice(1) : 
                      'N/A'
                    }
                  </span>
                </div>
              </div>
              <div className="pattern-item">
                <div className="pattern-label">Attendance Trend:</div>
                <div className="pattern-value">
                  <span className={`trend-badge ${attendanceRecord.patterns?.trend || 'stable'}`}>
                    {attendanceRecord.patterns?.trend ? 
                      attendanceRecord.patterns.trend.charAt(0).toUpperCase() + attendanceRecord.patterns.trend.slice(1) : 
                      'Stable'
                    }
                  </span>
                </div>
              </div>
              {attendanceRecord.patterns?.notes && (
                <div className="pattern-item full-width">
                  <div className="pattern-label">Notes:</div>
                  <div className="pattern-notes">
                    <p>{attendanceRecord.patterns.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="print-button" onClick={() => window.print()}>
            🖨️ Print
          </button>
          <button className="close-footer-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default AttendanceDetailView;
